import { useEffect, useRef } from 'react'
import { getMicFreqData } from '../micAudio'

const W = 320
const H = 190
const COLS = 22
const ROWS = 13
const FREQ_FRACTION = 0.34
const PUSH_INTERVAL = 0.06

// Mel warp: low frequencies (bottom rows) get stretched tall, highs squashed —
// same curve as the desktop scene, just baked into each row's pixel height.
const melWarp = (u) => Math.log(1 + u * 15) / Math.log(16)
const rowEdges = Array.from({ length: ROWS + 1 }, (_, i) => 1 - melWarp(i / ROWS))

const STOPS = [
  [0.02, 0.01, 0.09],
  [0.32, 0.05, 0.43],
  [0.79, 0.16, 0.32],
  [1.0, 0.52, 0.12],
  [1.0, 0.95, 0.74],
]
function magma(e) {
  e = Math.max(0, Math.min(1, e)) * (STOPS.length - 1)
  const i = Math.min(STOPS.length - 2, Math.floor(e))
  const f = e - i
  const r = STOPS[i][0] + (STOPS[i + 1][0] - STOPS[i][0]) * f
  const g = STOPS[i][1] + (STOPS[i + 1][1] - STOPS[i][1]) * f
  const b = STOPS[i][2] + (STOPS[i + 1][2] - STOPS[i][2]) * f
  return `rgb(${(r * 255) | 0},${(g * 255) | 0},${(b * 255) | 0})`
}

// Synthetic wandering-pitch speech: a harmonic stack + formant envelope,
// scrolling left → right, matching the desktop fallback.
function syntheticEnergy(colFrac, fn, t) {
  const tt = colFrac - t * 0.12
  const f0 = 0.045 + 0.022 * Math.sin(tt * 5.0 + t * 0.6)
  const gate = Math.max(0, Math.min(1, 0.55 + 0.6 * Math.sin(tt * 9.0 + t * 0.8)))
  let h = 0
  for (let k = 1; k <= 9; k++) {
    const d = fn - k * f0
    h += (1 / k) * Math.exp(-(d * d) / 0.00045)
  }
  const env =
    Math.exp(-((fn - 0.07) ** 2) / 0.004) +
    0.7 * Math.exp(-((fn - 0.2) ** 2) / 0.007) +
    0.45 * Math.exp(-((fn - 0.4) ** 2) / 0.012)
  const noise = 0.04 * Math.exp(-fn * 2.0)
  return (h * env * gate + noise) * 0.85
}

// The flat time × frequency × energy image an audio model actually reads.
// Real mic input pushes fresh FFT columns that scroll across; idle it falls
// back to a synthetic wandering-pitch voice with its harmonic stack.
export function Scene04Spectrogram2D() {
  const cellRefs = useRef([])
  const ring = useRef({ hist: new Float32Array(COLS * ROWS), head: 0, acc: 0 })
  const raf = useRef(0)

  useEffect(() => {
    let start = performance.now()
    let last = start
    const loop = (now) => {
      const t = (now - start) / 1000
      const delta = (now - last) / 1000
      last = now
      const freq = getMicFreqData()

      if (freq) {
        const r = ring.current
        r.acc += delta
        while (r.acc >= PUSH_INTERVAL) {
          r.acc -= PUSH_INTERVAL
          const base = r.head * ROWS
          for (let row = 0; row < ROWS; row++) {
            const fn = row / (ROWS - 1)
            const bin = Math.floor(fn * FREQ_FRACTION * (freq.length - 1))
            r.hist[base + row] = freq[bin] / 255
          }
          r.head = (r.head + 1) % COLS
        }
        for (let col = 0; col < COLS; col++) {
          const ringCol = (r.head + col) % COLS
          for (let row = 0; row < ROWS; row++) {
            const el = cellRefs.current[col * ROWS + row]
            if (el) el.setAttribute('fill', magma(r.hist[ringCol * ROWS + row] * 1.5))
          }
        }
      } else {
        for (let col = 0; col < COLS; col++) {
          const colFrac = col / (COLS - 1)
          for (let row = 0; row < ROWS; row++) {
            const fn = row / (ROWS - 1)
            const el = cellRefs.current[col * ROWS + row]
            if (el) el.setAttribute('fill', magma(syntheticEnergy(colFrac, fn, t)))
          }
        }
      }
      raf.current = requestAnimationFrame(loop)
    }
    raf.current = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf.current)
  }, [])

  const cellW = W / COLS

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', maxWidth: 340, height: 'auto', display: 'block', margin: '0 auto' }}>
      {Array.from({ length: COLS }).map((_, col) =>
        Array.from({ length: ROWS }).map((_, row) => {
          const y0 = rowEdges[row] * H
          const y1 = rowEdges[row + 1] * H
          return (
            <rect
              key={`${col}-${row}`}
              ref={(el) => { cellRefs.current[col * ROWS + row] = el }}
              x={col * cellW}
              y={Math.min(y0, y1)}
              width={cellW + 0.5}
              height={Math.abs(y1 - y0) + 0.5}
              fill="#050414"
            />
          )
        })
      )}
      <line x1="0" y1={H} x2={W} y2={H} stroke="#3a5a7a" strokeWidth="1" opacity="0.4" />
      <line x1="0" y1="0" x2="0" y2={H} stroke="#3a5a7a" strokeWidth="1" opacity="0.4" />
    </svg>
  )
}
